// apps/client/src/hooks/useCustomerSearch.ts
import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface CustomerSearchResult {
  customerName: string;
  loanCount: number;
  totalLoanValue: number;
}

interface UseCustomerSearchParams {
  searchTerm: string;
  excludeCustomers?: string[];
  enabled?: boolean;
}

interface UseCustomerSearchReturn {
  customers: CustomerSearchResult[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to search for customers by name (for client set creation UI)
 * Implements debouncing and request cancellation to avoid race conditions
 */
export function useCustomerSearch({
  searchTerm,
  excludeCustomers = [],
  enabled = true,
}: UseCustomerSearchParams): UseCustomerSearchReturn {
  const [customers, setCustomers] = useState<CustomerSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track request ID to ignore stale responses (AbortController doesn't work with httpsCallable)
  const requestIdRef = useRef(0);

  // Serialize excludeCustomers to avoid reference equality issues
  const excludeKey = excludeCustomers.join(',');

  useEffect(() => {
    // Don't search if disabled or search term is too short
    if (!enabled || !searchTerm || searchTerm.trim().length < 2) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    // Increment request ID - any in-flight request with lower ID is stale
    const currentRequestId = ++requestIdRef.current;

    // Set loading immediately (before debounce) to show loading state
    setLoading(true);

    // Debounce search
    const timeoutId = setTimeout(async () => {
      setError(null);

      try {
        console.log(`[useCustomerSearch] Searching for "${searchTerm}" (request #${currentRequestId})`);

        const searchCustomersFunc = httpsCallable<
          { searchTerm: string; excludeCustomers?: string[] },
          CustomerSearchResult[]
        >(functions, 'searchCustomers');

        const result = await searchCustomersFunc({
          searchTerm: searchTerm.trim(),
          excludeCustomers,
        });

        // CRITICAL: Only update state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          console.log(`[useCustomerSearch] Found ${result.data.length} customers (request #${currentRequestId})`);
          setCustomers(result.data);
          setLoading(false);
        } else {
          console.log(`[useCustomerSearch] Ignoring stale response (request #${currentRequestId}, current #${requestIdRef.current})`);
        }
      } catch (err) {
        // Only update error state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          console.error('[useCustomerSearch] Error searching customers:', err);
          setError(err instanceof Error ? err.message : 'Failed to search customers');
          setCustomers([]);
          setLoading(false);
        }
      }
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm, excludeKey, enabled]); // Use serialized excludeKey instead of array

  return {
    customers,
    loading,
    error,
  };
}
